<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'DunningLetterJobs',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    item: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const items = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    items: items.value,
    ux: ux.value,
  }));

  const $$load_items = async () => {
    return $fn.value
      .fetch('browse_a_job', {
        parameters: {
          oid: props.item.id,
          job_type: 'dunning_letters:pdf_generation,dunning_letters:emails',
        },
      })
      .then((d) => _.orderBy(d, ['number'], ['desc']));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'items' }, { name: 'ux' }],
    loaders: { items: $$load_items },
    ux: {},
    dataRefs: { items, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );

  const act_7315092382398562_reloadComponentData_2wwl8wzk0 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_b5diy7cte =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_2wwl8wzk0,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_b5diy7cte],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_b5diy7cte,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_b5diy7cte = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_b5diy7cte.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="container-fluid" v-if="loaded && items.length">
      <div v-for="job in items" :key="job.id" class="row">
        <div class="col-4 text-end pb-5 text-nowrap text-truncate">
          <div class="text-nowrap">
            #{{ job.number }}
            <ItpIcon v-if="job.job_type === 'email'" name="fa-envelope">
            </ItpIcon>

            <ItpIcon v-if="job.job_type === 'document'" name="fa-file-pdf">
            </ItpIcon>
          </div>

          <div class="text-muted text-nowrap" :title="datetime(job.created_at)">
            <ItpIcon name="fa-clock"></ItpIcon>
            {{ job.created_at | datetime({ distanceToNow: true }) }}
          </div>

          <div class="text-muted text-nowrap">
            {{ username(job.created_by) }}
          </div>
        </div>

        <div class="col-8 border-start pb-5 text-nowrap text-truncate">
          <div>
            <!--span :class="jobStatusBadgeClass(job.status)">
        {{pgettext('TaskRunStatus', job.status) }}
      </span-->

            <span
              class="ms-1 text-muted text-nowrap"
              v-if="job.updated_at"
              :title="datetime(job.updated_at)"
            >
              {{ job.updated_at | datetime({ distanceToNow: true }) }}
            </span>
          </div>

          <div
            v-if="job.job_type === 'email'"
            class="text-truncate"
            :title="job.mail_to"
          >
            <span class="text-muted me-1">
              {{ pgettext('email', 'To') }}:
            </span>
            {{
              $fn._.isArray(job.mail_to) ? job.mail_to.join('; ') : job.mail_to
            }}
          </div>

          <div
            v-if="job.job_type === 'email' && job.subject"
            class="text-truncate"
            :title="job.subject"
          >
            <span class="text-muted me-1">
              {{ pgettext('email', 'Subject') }}:
            </span>
            {{ job.subject }}
          </div>

          <div
            v-if="job.job_type === 'email' && job.tracking"
            :title="datetime(job.tracking.timestamp)"
          >
            <ItpIcon name="fa-eye"></ItpIcon>
            <span class="ms-2">
              {{ job.tracking.timestamp | datetime({ distanceToNow: true }) }}
            </span>
          </div>

          <hr v-if="job.log_url || job.pdf_url || job.html_url" class="my-2" />

          <div v-if="job.status === 'Errored'">
            <ItpLink
              :href="job.log_url"
              icon="fa-file-alt"
              :text="$i18n._('Error log')"
              :tooltip="job.log_filename"
              target="_blank"
              class="d-block"
              use-auth
            ></ItpLink>
          </div>

          <div v-if="job.status === 'Completed'">
            <ItpLink
              v-if="job.pdf_url"
              :href="job.pdf_url"
              icon="fa-file-pdf"
              text="PDF"
              :tooltip="job.pdf_filename"
              target="_blank"
              class="d-block"
              use-auth
            ></ItpLink>

            <ItpLink
              v-if="job.html_url"
              :href="job.html_url"
              icon="fa-file-code"
              text="HTML"
              :tooltip="job.html_filename"
              target="_blank"
              class="d-block"
              use-auth
            ></ItpLink>
          </div>
        </div>
      </div>
    </div>
  </ItpUsercontrol>
</template>
